import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { actorArt } from './actors';
import { TILE_W, TILE_H } from './iso';

interface Door { root: Container }
interface Citizen {
  root: Container; spr: Sprite; dust: Graphics;
  gx: number; gy: number; dx: number; dy: number;
  timer: number; clock: number; job: number; home: boolean;
}

/** A fixed pool of pedestrians. All movement uses simulation seconds. */
export class Citizens {
  readonly pool: Citizen[] = [];
  count = 0;
  private age = -1;
  private sheltered = false;
  private nextBuilder = 0;
  private walkFrames: Texture[] = [];
  private workFrames: Texture[] = [];

  constructor(layer: Container) {
    for (let i = 0; i < 14; i++) {
      const root = new Container();
      root.eventMode = 'none';
      const spr = new Sprite(actorArt('citizen', 0));
      spr.anchor.set(.5, 26 / 28);
      const shadow = new Graphics().ellipse(0, 1, 7, 3).fill({color: 0x000000, alpha: .2});
      const dust = new Graphics().rect(-7, 0, 3, 2).rect(5, -2, 2, 2).fill(0xd1a56e);
      dust.visible = false;
      root.addChild(shadow, spr, dust);
      root.visible = false;
      layer.addChild(root);
      this.pool.push({root, spr, dust, gx: 0, gy: 0, dx: 0, dy: 0, timer: i * .23, clock: i, job: -1, home: true});
    }
  }

  reset(): void {
    this.sheltered = false;
    for (const c of this.pool) {
      c.gx = c.gy = c.dx = c.dy = 0;
      c.job = -1; c.home = true; c.timer = c.clock % 1;
      c.root.alpha = 1; c.spr.rotation = 0;
    }
  }

  shelter(value: boolean): void {
    this.sheltered = value;
    for (const c of this.pool) {
      c.dx = c.dy = 0; c.job = -1; c.home = true; c.timer = 0;
    }
  }

  build(index: number): void {
    if (this.sheltered || !this.count) return;
    const c = this.pool[this.nextBuilder++ % this.count];
    c.job = index; c.home = false; c.timer = 0;
  }

  update(population: number, age: number, dt: number, doors: readonly Door[], omen: boolean): void {
    this.count = Math.max(2, Math.min(14, Math.ceil(population / 2)));
    if (this.age !== age) {
      this.age = age;
      for (let f = 0; f < 4; f++) {
        this.walkFrames[f] = actorArt('citizen', age, f);
        this.workFrames[f] = actorArt('citizen', age, f, 'attack');
      }
      for (const c of this.pool) c.spr.texture = actorArt('citizen', age);
    }
    for (let i = 0; i < 14; i++) {
      const c = this.pool[i];
      c.root.visible = i < this.count;
      if (!c.root.visible) continue;
      if (dt > 0) {
        c.clock += dt;
        if (this.sheltered) { c.dx = c.dy = 0; }
        else if (c.job >= 0 && doors[c.job]) {
          const door = doors[c.job].root;
          c.dx = door.x / TILE_W + door.y / TILE_H + .65;
          c.dy = door.y / TILE_H - door.x / TILE_W + .65;
        }
        const arrived = Math.abs(c.dx - c.gx) + Math.abs(c.dy - c.gy) < .02;
        if (arrived) {
          if (this.sheltered) c.root.alpha = Math.max(0, c.root.alpha - dt * 3);
          else if (!omen) {
            c.timer -= dt;
            if (c.timer <= 0) {
              if (c.job >= 0) { c.job = -1; c.timer = 1.5; c.home = true; c.dx = c.dy = 0; }
              else if (c.home) {
                // Every other trip leaves the dense quarter along a street axis, so the
                // town reads as inhabited from the outside, not just between doors.
                const trip = i + Math.floor(c.clock / 3);
                const door = doors.length && trip % 2 ? doors[trip % doors.length].root : null;
                if (door) {
                  c.dx = door.x / TILE_W + door.y / TILE_H + .65;
                  c.dy = door.y / TILE_H - door.x / TILE_W + .65;
                } else {
                  const reach = 2.5 + (trip * 7 % 4), sign = (trip >> 1) % 2 ? 1 : -1;
                  c.dx = i % 2 ? sign * reach : 0;
                  c.dy = i % 2 ? 0 : sign * reach;
                }
                c.home = false; c.timer = 1.2 + i * .09;
              } else { c.dx = c.dy = 0; c.home = true; c.timer = .7; }
            }
          }
        } else if (!omen || this.sheltered) {
          // Follow the central street, then the lane beside the building doors.
          const speed = dt * (this.sheltered ? 6 : 1.5);
          let tx = c.dx, ty = c.dy;
          if (Math.abs(c.gx - c.dx) > .02) {
            if (Math.abs(c.gy) > .02) { tx = c.gx; ty = 0; }
            else ty = 0;
          }
          const distance = Math.hypot(tx - c.gx, ty - c.gy);
          if (distance > 0) {
            const k = Math.min(1, speed / distance);
            c.gx += (tx - c.gx) * k; c.gy += (ty - c.gy) * k;
          }
          if (c.job >= 0) c.timer = 1.5;
        }
        if (!this.sheltered) c.root.alpha = Math.min(1, c.root.alpha + dt * 2);
        const working = arrived && c.job >= 0 && !omen && !this.sheltered;
        c.spr.texture = (working ? this.workFrames : this.walkFrames)[omen ? 0 : Math.floor(c.clock * (arrived ? 2 : 8)) % 4];
        c.spr.y = omen ? -2 : Math.round(Math.sin(c.clock * (working ? 18 : arrived ? 2 : 10)) * (working ? 2 : 1));
        c.dust.visible = working;
        c.dust.alpha = .3 + .5 * Math.abs(Math.sin(c.clock * 12));
      }
      c.root.x = (c.gx - c.gy) * TILE_W / 2;
      c.root.y = (c.gx + c.gy) * TILE_H / 2;
      c.root.zIndex = (c.gx + c.gy) * 10 + 3;
    }
  }
}
